import { oc } from '@orpc/contract';
import { ACCOUNT_STATUSES, ROUTED_TO, TICKET_STATUSES, WAITING_ON } from '@lsc/domain';
import { z } from 'zod';
export const accountGetInput = z.object({ accountId: z.uuid() });
const isoDate = z.string().nullable();
export const accountLocation = z.object({
    accountId: z.uuid(),
    label: z.string(),
    mid: z.string().nullable(),
    status: z.enum(ACCOUNT_STATUSES),
    city: z.string().nullable(),
    csPhone: z.string().nullable(),
});
export const accountPerson = z.object({
    contactId: z.uuid().nullable(),
    name: z.string(),
    role: z.string().nullable(),
    email: z.string().nullable(),
    phones: z.array(z.object({ e164: z.string(), kind: z.string() })),
    /** `principal` rows come from the processor (signer, guarantor); `contact` rows from calls and imports. */
    kind: z.enum(['principal', 'contact']),
    isSigner: z.boolean().nullable(),
    ownershipPct: z.number().int().nullable(),
    /** The newest ticket this person was the caller on (ISO), when there is one. */
    lastTicketAt: z.string().nullable(),
    /** Where the merged entries came from: processor, slack_import, goto_import, desk, ... */
    sources: z.array(z.string()),
    /** On file only: phones and email reps typed for this person on tickets (not verified). */
    fromTickets: z.object({ phones: z.array(z.object({ e164: z.string(), kind: z.string() })), email: z.string().nullable() }).nullable(),
    /** Callers only: the person on file who shares this bare first name, shown as a hint, never merged. */
    sameFirstNameAs: z.string().nullable(),
});
/** On file (processor, verified) apart from callers typed on past tickets; hidden lists placeholder and store names dropped. */
export const accountPeople = z.object({ onFile: z.array(accountPerson), callers: z.array(accountPerson), hidden: z.number().int(), hiddenNames: z.array(z.string()) });
export const accountEvent = z.object({
    id: z.number().int(),
    kind: z.string(),
    severity: z.enum(['info', 'warn', 'high']),
    occurredAt: z.string(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
});
/** One ticket as the account page lists it. Only this account's tickets (decision D3): a chain's siblings are one click away. */
export const accountTicketRow = z.object({
    ticketId: z.uuid(),
    ticketNumber: z.string(),
    subject: z.string(),
    status: z.enum(TICKET_STATUSES),
    routedTo: z.enum(ROUTED_TO).nullable(),
    waitingOn: z.enum(WAITING_ON).nullable(),
    createdAt: z.string(),
    /** `closed_at`, or `resolved_at` for a ticket that is resolved but not yet closed. */
    closedAt: z.string().nullable(),
    loggedByName: z.string().nullable(),
    resolution: z.string().nullable(),
});
export const accountTickets = z.object({
    /** Live tickets (open, in progress, waiting, stuck), oldest first. */
    open: z.array(accountTicketRow),
    /** The last five resolved or closed, newest first; `closedCount` says how many there really are. */
    recentClosed: z.array(accountTicketRow),
    openCount: z.number().int(),
    closedCount: z.number().int(),
});
export const accountDetail = z.object({
    accountId: z.uuid(),
    label: z.string(),
    dbaName: z.string().nullable(),
    legalName: z.string().nullable(),
    legalEntityId: z.uuid().nullable(),
    /** The grouping was made on the desk (Same owner), so its stores can be separated here. */
    ownerByDesk: z.boolean(),
    processor: z.string(),
    env: z.string(),
    mid: z.string().nullable(),
    tsysMidCore: z.string().nullable(),
    status: z.enum(ACCOUNT_STATUSES),
    rawStatus: z.string().nullable(),
    statusChangedAt: isoDate,
    riskLevel: z.string().nullable(),
    mcc: z.string().nullable(),
    processorType: z.string().nullable(),
    boardedOn: isoDate,
    closedOn: isoDate,
    firstBatchOn: isoDate,
    lastBatchOn: isoDate,
    csPhone: z.string().nullable(),
    csEmail: z.string().nullable(),
    address: z.object({ street: z.string().nullable(), city: z.string().nullable(), zip: z.string().nullable() }).nullable(),
    rails: z.array(z.object({ rail: z.string(), mid: z.string().nullable(), status: z.string().nullable(), lastBatchOn: isoDate })),
    /** Every account under the same legal entity, this one included, live first. */
    locations: z.array(accountLocation),
    people: accountPeople,
    events: z.array(accountEvent),
    tickets: accountTickets,
    lastSeenAt: isoDate,
});
export const accountGet = oc.route({ method: 'POST', path: '/accounts/get', summary: 'One account with its entity, locations, people and recent events' }).input(accountGetInput).output(accountDetail.nullable());
//# sourceMappingURL=account-detail.js.map